import React, { useState, useEffect, createContext, useContext } from 'react';
import API from '../../Services/API';
import { useToasts } from '../Toasts';
import { classNames, isLocal, useVisibilityChange } from '../../Services/helpers';
import { captureException } from '../../Services/Sentry';
import DB from '../../Data/DB';
import OfflineTaskQueue from './OfflineQueue';
import { usePreference } from '../../Data/Preference';
import Callback from '../../Services/Callback';
import { offlineStatus } from '.';

const OfflineContext = createContext();
const backOnline = new Callback();

export const useOfflineContext = () => {
    return useContext(OfflineContext);
};

export const Provider = ({ children }) => {
    const {showSuccess, showError} = useToasts();
    const [overrideIsOnline, setOverrideIsOnline] = usePreference('overrideIsOnline', null, true);
    const [isOnline, setIsOnline] = useState(overrideIsOnline !== null ? overrideIsOnline : navigator.onLine);
    const [offlineSupported, setOfflineSupported] = useState(DB.ready);
    const [status, setStatus] = useState(null);
    const [failedJobs, setFailedJobs] = useState([]);
    const [errorsOpen, setErrorsOpen] = useState(false);

    useEffect(() => {
        (async () => {
            if (DB.ready) {
                await OfflineTaskQueue.init();
                setOfflineSupported(DB.ready);
                updateStatus();
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [DB.ready]);

    useEffect(() => {
        backOnline.listen(() => {
            setTimeout(() => {
                sync();
            }, 100);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateOnline = () => {
        setIsOnline(prev => {
            const updated = overrideIsOnline !== null ? overrideIsOnline : navigator.onLine
            if (prev === false && updated === true) {
                backOnline.invoke();
            }
            return updated;
        });
    }

    const updateStatus = () => {
        if (!isOnline) {
            setStatus(offlineStatus.OFFLINE);

        } else if (OfflineTaskQueue.syncing) {
            setStatus(offlineStatus.SYNCING);

        } else if (OfflineTaskQueue.status.failed > 0) {
            setStatus(offlineStatus.ERROR);

        } else if (OfflineTaskQueue.status.pending > 0) {
            setStatus(offlineStatus.PENDING);

        } else if (OfflineTaskQueue.status.synced > 0) {
            setStatus(offlineStatus.COMPLETE);

        } else  {
            setStatus(null);
        }
    }

    useVisibilityChange(() => {
        setTimeout(() => {
            updateOnline();
        }, 100);
    });

    useEffect(() => {
        setTimeout(() => {
            updateStatus();
            setFailedJobs(OfflineTaskQueue.listFailed());
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOnline, OfflineTaskQueue.syncing, OfflineTaskQueue.status])

    useEffect(() => {
        updateOnline();

        const updateInterval = setInterval(() => {
            updateOnline();
        }, 10000);

        window.addEventListener('online', updateOnline);
        window.addEventListener('offline', updateOnline);

        window.onlineOverride = setOverrideIsOnline;

        return () => {
            window.removeEventListener('online', updateOnline);
            window.removeEventListener('offline', updateOnline);
            clearInterval(updateInterval);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [overrideIsOnline]);

    const queueTask = (task, entity, data, message) => {
        return new Promise((resolve, reject) => {
            OfflineTaskQueue.add(task, entity, data)
                .then(() => {
                    showSuccess(message);
                    resolve();
                })
                .catch(e => {
                    console.error('Error queuing task', e);
                    showError('Error queuing task', e.message);
                    captureException(e);
                    reject(e);
                });
        });
    }

    const hasQueuedTask = (task, entity) => {
        if (!offlineSupported) {
            return false;
        }

        return OfflineTaskQueue.has(task, entity);
    }

    const getQueuedTask = (task, entity) => {
        if (!offlineSupported) {
            return null;
        }

        return OfflineTaskQueue.get(task, entity);
    }

    const removeQueuedTask = (task, entity) => {
        if (!offlineSupported) {
            return null;
        }

        return OfflineTaskQueue.remove(task, entity);
    }

    const sync = async () => {
        if (!isOnline && !isLocal() || !offlineSupported) {
            return;
        }

        await OfflineTaskQueue.sync();
    }

    const providerValues = {
        isOnline,
        queueTask,
        offlineSupported,
        hasQueuedTask,
        getQueuedTask,
        removeQueuedTask,
        sync,
        isSyncing: OfflineTaskQueue.syncing,
        backOnline,
        status,
        failedJobs,
        errorsOpen,
        setErrorsOpen,
    };

    return (
        <OfflineContext.Provider value={providerValues}>
            {children}
            <OnlineToggle {...{overrideIsOnline, setOverrideIsOnline, local: isLocal(), isAdmin: API.isAdmin('Super')}} />
        </OfflineContext.Provider>
    );
};

const OnlineToggle = ({overrideIsOnline, setOverrideIsOnline, local, isAdmin}) => {
    if (!local || !isAdmin) {
        return null;
    }

    const toggle = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (overrideIsOnline === true) {
            setOverrideIsOnline(false);
        } else if (overrideIsOnline === false) {
            setOverrideIsOnline(null);
        } else {
            setOverrideIsOnline(true);
        }
    }

    return <button className={classNames(
        'fixed top-0 right-0 z-99',
        'm-0 h-3 w-3',
        overrideIsOnline === true && 'bg-green-600',
        overrideIsOnline === false && 'bg-red-600',
        overrideIsOnline === null && 'bg-gray-400',
        )} onClick={toggle}>
        {/* {overrideIsOnline === true && 'Online'}
        {overrideIsOnline === false && 'Offline'}
        {overrideIsOnline === null && 'Default'} */}
    </button>
}
